<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card mt-5">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span class="mt-3 fs-2">Notification Event</span>
          <b-button variant="primary" @click="addEvent">
            <span class="fe fe-file-plus"></span>
            Add Notification Event
          </b-button>
        </div>

        <div class="d-flex justify-content-end align-items-center m-3 gap-3">
          <div class="d-flex justify-content-center align-items-center gap-3">
            <label class="align-self-center m-0">Search</label>
            <b-form-input class="w-auto" v-model="filters.search" placeholder="Type Here..." @keyup.enter="onSearch" />
          </div>
          <div class="col-lg-2 px-0">
            <v-select
              v-model="filters.tenant"
              :options="tenantOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Tenant"
            />
          </div>
          <div class="col-lg-3 px-0">
            <v-select
              v-model="filters.notification_trigger_id"
              :options="notificationTriggers"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Trigger"
            />
          </div>
          <div class="col-lg-2 px-0">
            <v-select
              v-model="filters.module_type_id"
              :options="moduleTypeOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Module"
            />
          </div>
          <b-button variant="primary" @click="clearFilter">
            <span class="fe fe-x-square"></span>
            Clear Filter
          </b-button>
        </div>

        <NotificationEventTable
          :rows="notificationEvents.data"
          :total="notificationEvents.total"
          :showing-from="showingFrom"
          :showing-to="showingTo"
          :page="filters.page"
          :page-size="filters.pageSize"
          @sort-change="onSortChange"
          @page-change="onPageChange"
          @per-page-change="onPerPageChange"
          @page-change-from-bv="onPageChangeFromBv"
          @update="updateData"
          @view="viewDetails"
          @delete="deleteData"
          @duplicate="duplicateData"
        />
        <NotificationEventModal
          :modal-id="'notification-event-modal'"
          :is-edit="isEdit"
          :is-view="isView"
          :tenant-options="tenantOptions"
          :trigger-options="notificationTriggers"
          :recipient-options="notificationRecipients"
          :data="modalData"
          @save="onSaveModal"
          @close="showModal = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import NotificationEventModal from "./notification-event-modal.vue";
import NotificationEventTable from "./notification-event-table.vue";
import toast from "@/utils/toast";

export default {
  name: "NotificationEventMain",

  components: {
    NotificationEventModal,
    NotificationEventTable,
  },

  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        tenant: null,
        search: "",
        notification_trigger_id: null,
        module_type_id: null,
      },
      isLoading: false,
      tenantOptions: [
        { id: "bib-academy", name: "BIB Academy" },
        { id: "ugems-university", name: "UGEMS University" },
      ],
      _searchTimer: null,
      showModal: false,
      isEdit: false,
      isView: false,
      selectedEventId: null,
      modalData: null,
      notificationTriggers: [],
      notificationRecipients: [],
      moduleTypeOptions: [],
    };
  },

  computed: {
    ...mapGetters("notificationEvent", ["notificationEvents"]),

    showingFrom() {
      if (!this.notificationEvents.total) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.notificationEvents.total ? this.notificationEvents.total : to;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    "filters.tenant"() {
      this.filters.page = 1;
      this.loadData();
    },

    "filters.notification_trigger_id"() {
      this.filters.page = 1;
      this.loadData();
    },

    "filters.module_type_id"() {
      this.filters.page = 1;
      this.loadData();
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("notificationEvent", [
      "getNotificationEvents",
      "getNotificationEventDetail",
      "createNotificationEvent",
      "updateNotificationEvent",
      "deleteNotificationEvent",
      "duplicateNotificationEvent",
      "getNotificationTriggers",
      "getNotificationRecipients",
      "getModuleTypes",
    ]),

    cleanObject(obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(
          ([, v]) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)
        )
      );
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        tenant: this.filters.tenant,
        notification_trigger_id: this.filters.notification_trigger_id,
        module_type_id: this.filters.module_type_id,
        search: this.filters.search,
        all: this.filters.all,
      };
      return this.cleanObject(raw);
    },

    async loadData() {
      this.isLoading = true;
      try {
        const query = this.buildQueryFromFilters();
        await this.getNotificationEvents(query);
        if (this.notificationEvents.page) this.filters.page = this.notificationEvents.page;
        if (this.notificationEvents.limit) this.filters.pageSize = this.notificationEvents.limit;
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = {
        ...this.filters,
        search: "",
        tenant: null,
        page: 1,
        notification_trigger_id: null,
        module_type_id: null,
      };
      this.loadData();
    },

    async addEvent() {
      this.isEdit = false;
      this.isView = false;
      this.modalData = null;
      this.showModal = true;
      this.$bvModal.show("notification-event-modal");
    },

    async updateData(row) {
      this.isEdit = true;
      this.isView = false;
      this.selectedEventId = row.id;
      this.showModal = true;

      const detail = await this.getNotificationEventDetail(row.id);
      this.modalData = detail;

      this.$nextTick(() => {
        this.$bvModal.show("notification-event-modal");
      });
    },

    async deleteData(row) {
      const id = row.id ?? row;

      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Tindakan ini tidak dapat dibatalkan!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.deleteNotificationEvent(id);

          const newTotal = this.notificationEvents.total - 1;
          const maxPage = Math.max(1, Math.ceil(newTotal / this.filters.pageSize));

          if (this.filters.page > maxPage) this.filters.page = maxPage;

          this.loadData();

          toast({ type: "success", title: "Success", message: "Notification event berhasil dihapus" });
        }
      });
    },

    async duplicateData(row) {
      const id = row.id ?? row;

      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin menduplikasi ini?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, duplikasi",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.duplicateNotificationEvent(id);

            const newTotal = this.notificationEvents.total - 1;
            const maxPage = Math.max(1, Math.ceil(newTotal / this.filters.pageSize));

            if (this.filters.page > maxPage) this.filters.page = maxPage;

            this.loadData();

            toast({ type: "success", title: "Success", message: "Notification event berhasil diduplikasi" });
          } catch (error) {
            const msg =
              error?.response?.data?.error || error?.message || "Terjadi kesalahan while duplicating notification event.";

            toast({ type: "error", title: "Error", message: msg });
          }
        }
      });
    },

    async viewDetails(row) {
      this.isEdit = false;
      this.isView = true;
      this.selectedEventId = row.id;
      this.showModal = true;

      const detail = await this.getNotificationEventDetail(row.id);
      this.modalData = detail;

      this.$nextTick(() => {
        this.$bvModal.show("notification-event-modal");
      });
    },

    updateUrlQuery(filters) {
      const query = this.cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        tenant: filters.tenant,
        notification_trigger_id: filters.notification_trigger_id,
        module_type_id: filters.module_type_id,
        search: filters.search,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        tenant: query.tenant || null,
        search: query.search || "",
      };

      this.filters = { ...this.filters, ...parsed };
    },

    async onSaveModal(data) {
      try {
        if (this.isEdit) {
          await this.updateNotificationEvent({
            id: this.selectedEventId,
            data,
          });

          toast({ type: "success", title: "Success", message: "Notification event berhasil diperbarui" });
        } else {
          await this.createNotificationEvent(data);

          toast({ type: "success", title: "Success", message: "Notification event berhasil dibuat" });
        }

        this.$bvModal.hide("notification-event-modal");
        this.loadData();
      } catch (error) {
        const msg =
          error?.response?.data?.error || error?.message || "Terjadi kesalahan while saving notification event.";

        toast({ type: "error", title: "Error", message: msg });
      }
    },

    async loadNotificationTriggers() {
      try {
        const triggers = await this.getNotificationTriggers();
        this.notificationTriggers = triggers;
      } catch (err) {
        console.error("Failed to load triggers:", err);
      }
    },

    async loadModuleTypes() {
      try {
        const moduleTypes = await this.getModuleTypes();
        this.moduleTypeOptions = moduleTypes;
      } catch (err) {
        console.error("Failed to load triggers:", err);
      }
    },

    async loadRecipients() {
      try {
        const recipients = await this.getNotificationRecipients();
        this.notificationRecipients = recipients;
      } catch (err) {
        console.error("Failed to load recipients:", err);
      }
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await this.loadModuleTypes();
    await this.loadNotificationTriggers();
    await this.loadRecipients();
    await this.loadData();
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
  },
};
</script>

<style scoped>
.table-header {
  white-space: nowrap;
}

.w-action {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

.vgt-table.table-auto table {
  table-layout: auto !important;
}
</style>
